import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MAIN_SITE_URL } from '@/data/categories';

const whyFeatures = [
  { icon: 'ri-layout-grid-line', titleKey: 'home.why.f1title', textKey: 'home.why.f1text' },
  { icon: 'ri-flashlight-line', titleKey: 'home.why.f2title', textKey: 'home.why.f2text' },
  { icon: 'ri-refresh-line', titleKey: 'home.why.f3title', textKey: 'home.why.f3text' },
  { icon: 'ri-heart-line', titleKey: 'home.why.f4title', textKey: 'home.why.f4text' },
];

function FaqAccordion() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(0);

  const items = [1, 2, 3, 4, 5].map((i) => ({
    q: t(`home.faq.q${i}`),
    a: t(`home.faq.a${i}`),
  }));

  return (
    <div className="mx-auto mt-10 max-w-3xl divide-y divide-background-200/70 rounded-lg border border-background-200/70 bg-background-50">
      {items.map((item, idx) => {
        const isOpen = open === idx;
        return (
          <div key={idx}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : idx)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-background-100/60"
            >
              <span className="text-sm font-semibold text-foreground-900 md:text-base">{item.q}</span>
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-foreground-500 transition-transform ${
                  isOpen ? 'rotate-180 bg-primary-100 text-primary-700' : 'bg-background-100'
                }`}
              >
                <i className="ri-arrow-down-s-line text-sm" />
              </span>
            </button>
            {isOpen && (
              <div className="px-5 pb-5">
                <p className="text-sm leading-relaxed text-foreground-600 md:text-base">{item.a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function InfoSections() {
  const { t } = useTranslation();

  return (
    <>
      {/* What Is */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{t('home.what.title')}</h2>
            <p className="mt-5 text-base leading-relaxed text-foreground-600">{t('home.what.p1')}</p>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">{t('home.what.p2')}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className="ri-football-line text-xl" />
              </span>
              <p className="mt-4 font-heading text-3xl font-bold text-foreground-950">14+</p>
              <p className="mt-1 text-sm text-foreground-600">{t('home.categories.title')}</p>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-global-line text-xl" />
              </span>
              <p className="mt-4 font-heading text-3xl font-bold text-foreground-950">4</p>
              <p className="mt-1 text-sm text-foreground-600">{t('common.language')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="bg-background-100/60 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{t('home.why.title')}</h2>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {whyFeatures.map((f) => (
              <div key={f.titleKey} className="rounded-lg border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${f.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">{t(f.titleKey)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t(f.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Guide */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{t('home.guide.title')}</h2>
        <div className="mx-auto mt-8 max-w-3xl overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
          <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center md:p-8">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-book-open-line text-2xl" />
            </span>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold text-foreground-900 md:text-xl">{t('home.guide.cardTitle')}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t('home.guide.cardDesc')}</p>
              <p className="mt-3 text-xs text-foreground-500">
                <i className="ri-time-line mr-1" />
                {t('home.guide.cardDate')}
              </p>
            </div>
            <Link
              to="/usfans-spreadsheet-guide"
              className="inline-flex shrink-0 items-center justify-center rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              {t('home.guide.cardCta')}
              <i className="ri-arrow-right-line ml-1.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-background-100/60 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{t('home.faq.title')}</h2>
          <FaqAccordion />
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 px-6 py-12 text-center md:py-16">
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">{t('home.cta.title')}</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-background-50/90 md:text-base">{t('home.cta.text')}</p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex w-full items-center justify-center rounded-md bg-background-50 px-6 py-3 text-base font-semibold text-foreground-900 transition-colors hover:bg-background-100 whitespace-nowrap sm:w-auto"
            >
              {t('home.cta.button')}
            </a>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex w-full items-center justify-center rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-base font-semibold text-background-50 transition-colors hover:bg-background-50/20 whitespace-nowrap sm:w-auto"
            >
              {t('home.cta.button2')}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}